import { Grid } from '@mui/material';
import React, { useRef } from 'react';
import { useTranslation } from 'react-i18next';

import { Collapse } from '@work4all/components/lib/components/collapse';
import { EntityPickerPopover } from '@work4all/components/lib/components/entity-picker/components';
import { ServiceContractGroupPicker } from '@work4all/components/lib/components/entity-picker/entity-group-picker/EntityGroupPicker';
import { PROJECT_DATA } from '@work4all/components/lib/components/entity-picker/project-picker/projectPickerPresets';
import { useProjectPickerFilter } from '@work4all/components/lib/hooks';

import { LookupType } from '@work4all/models/lib/Enums/LookupType.enum';

import { ContractPickerField } from '../../../../../../../../../../components/entity-picker/ContractPickerField';
import { LookUpPickerField } from '../../../../../../../../../../components/entity-picker/LookUpPickerField';
import { ProjectPickerField } from '../../../../../../../../../../components/entity-picker/ProjectPickerField';
import { ControllerPlus } from '../../../../../../../../form-plus/controller-plus';
import { useFormContextPlus } from '../../../../../../../../form-plus/use-form-context-plus';
import { PickerTargetButton } from '../../../../../../../../locked-inputs';
import { ServiceContractsFormValue } from '../../../../../types';

export const Organisation: React.FC = () => {
  const { t } = useTranslation();

  const { control, watch } = useFormContextPlus<ServiceContractsFormValue>();

  const mainAddressId = watch('mainAddressSdObjMemberId');
  const endCustomerId = watch('endCustomerSdObjMemberId');

  // Use mainAddressId as primary, fallback to endCustomerId
  const businessPartnerId = mainAddressId || endCustomerId;

  // Project prefilter logic for customer/business partner
  const projectFilter = useProjectPickerFilter(businessPartnerId);

  const groupPopoverRef = useRef<EntityPickerPopover>(null);

  return (
    <Collapse defaultOpen={true} title={t('MASK.ORGANIZATION')}>
      <Grid container spacing="1rem">
        <Grid item xs={12}>
          <ControllerPlus
            control={control}
            name="project"
            render={({ field }) => (
              <ProjectPickerField
                {...field}
                data={PROJECT_DATA}
                {...projectFilter}
                excludeClosedStatus={projectFilter.prefilter.length > 0}
                clearable
              />
            )}
          />
        </Grid>
        <Grid item xs={12} sm={6}>
          <ControllerPlus
            name="contract"
            control={control}
            render={({ field }) => (
              <ContractPickerField
                {...field}
                sortBy="date"
                prefilter={[
                  {
                    $and: [
                      { isClosed: { $eq: false } },
                      {
                        $or: [
                          {
                            businessPartnerId: { $eq: mainAddressId },
                          },
                          {
                            businessPartnerId: { $eq: endCustomerId },
                          },
                        ],
                      },
                    ],
                  },
                ]}
              />
            )}
          />
        </Grid>
        <Grid item xs={6} sm={3}>
          <ControllerPlus
            name="group"
            control={control}
            render={({ field }) => {
              return (
                <EntityPickerPopover
                  picker={
                    <ServiceContractGroupPicker
                      {...field}
                      value={[field?.value]}
                      onChange={(value) => {
                        field.onChange(value?.[0] || null);
                        groupPopoverRef.current?.close();
                      }}
                      multiple={false}
                    />
                  }
                  autoclose
                >
                  <PickerTargetButton
                    onClear={() => {
                      field?.onChange(null);
                    }}
                    label={t('INPUTS.GROUP')}
                    value={field?.value?.name || ''}
                  />
                </EntityPickerPopover>
              );
            }}
          />
        </Grid>
        <Grid item xs={6} sm={3}>
          <ControllerPlus
            control={control}
            name="typeOfContract"
            render={({ field }) => {
              return (
                <LookUpPickerField
                  label={t('COMMON.KIND')}
                  lookupType={LookupType.WARTUNGSOBJEKT_ARTEN}
                  multiple={false}
                  layout="simple"
                  {...field}
                />
              );
            }}
          />
        </Grid>
      </Grid>
    </Collapse>
  );
};
